/* Персонажи каталога сервера в форме персонажей шаблона (CHARS в assets/bot-demo.js): вкладка «Персонажи» «Озвучки», карточка персонажа
   в панели, поиск, голос персонажа, сторис онбординга. Персонаж — отдельная сущность каталога (/app/catalog/avatars), голоса знают его по character_id.
   Портрет — собственная картинка персонажа или актёра (image_url каталога, D-272), а не лицо голоса: у голоса аватарка — его энсо.
   Большое описание, роль и характер приходят в карточке персонажа (/app/catalog/avatars/{id}) и догружаются при открытии. */
import { endpoints } from '../api';
import { accountLocale } from '../i18n';
import { isActor, type AvatarCatalog, type AvatarDetails } from '../api/avatar-catalog';
import { leaseArtwork, type ArtworkLease } from '../features/studio/studio-artwork-cache';
import { safeMediaUrl } from '../features/studio/studio-content';

/** Каталог персонажей знает ru и en: русский профиль — ru, остальные языки — en */
const catalogLocale = (): 'ru' | 'en' => (accountLocale() === 'ru' ? 'ru' : 'en');

export interface ShellChar {
  id: number;
  name: string;
  full: string;
  src: string;
  /** Ключ картинки для ASSET(): window.__ASSET_URL[img] — адрес портрета */
  img: string;
  y: number;
  sex: string;
  role: string;
  temper: string;
  short: string;
  long: string[];
  voices: number[];
  c: string[];
  /** Подборки голосов с персонажем (voice_collection_ids): раздел «Персонажи» открытой подборки; у шаблона поля нет — тогда c */
  vc?: string[];
  orb: string;
  /** Карточка персонажа (длинное описание, роль, характер) уже пришла */
  details: boolean;
  /** Реальный человек (D-270): вкладка «Актёры», шапка карточки «Актёр STEOS»; в ряды лиц не попадает */
  actor: boolean;
}

/** Сдвиг портрета по вертикали: сервер его не передаёт, у портретов шаблона он 14–22 % */
const PORTRAIT_Y = 17;

const assetUrls = () => {
  const w = window as unknown as { __ASSET_URL?: Record<string, string> };
  return (w.__ASSET_URL ||= {});
};

export function toShellChars(catalog: AvatarCatalog, _portrait?: (url: string | null, y: number) => string | null): ShellChar[] {
  return catalog.avatars.map(card => {
    const img = `srv-char-${card.id}`;
    /* портрет придёт картинкой каталога (leasePortraits); прежний адрес ключа (прошлая сессия) не показывается */
    delete assetUrls()[img];
    return {
      id: card.id,
      name: card.name,
      full: card.full_name || card.name,
      src: card.source,
      img,
      y: PORTRAIT_Y,
      sex: card.sex ?? '',
      role: '',
      temper: '',
      short: card.short_description,
      long: [],
      voices: card.voice_ids,
      /* подборки персонажа (поле c шаблона): роль рилса и ряд лиц берут персонажей подборки (bot-people.js chars) */
      c: (card.collection_ids ?? []).map(String),
      vc: card.voice_collection_ids?.map(String),
      /* до прихода картинки — пустой круг поля, как у пустой ячейки шаблона */
      orb: 'var(--field)',
      details: false,
      actor: isActor(card),
    };
  });
}

/** Удерживаемые портреты каталога (до следующей загрузки персонажей) */
let leases: ArtworkLease[] = [];
/** Портрет персонажа (D-272): публичный https картинки каталога как есть, иначе картинка через авторизованный BFF
    (/app/catalog/avatars/{id}/image) общим кэшем студии (leaseArtwork) — в __ASSET_URL встаёт blob, ключ хранилища
    (live-avatar/…) в <img> не попадает никогда. Картинка не пришла — круг поля, как раньше. */
export function leasePortraits(catalog: AvatarCatalog, chars: ShellChar[], portrait: (url: string | null, y: number) => string | null) {
  leases.forEach(lease => lease.release());
  leases = [];
  const epoch = endpoints.sessionEpoch?.() ?? 0;
  catalog.avatars.forEach((card, index) => {
    const c = chars.find(item => item.id === card.id);
    if (!c || assetUrls()[c.img]) return;
    const direct = safeMediaUrl(card.image_url);
    if (direct) {
      /* карточка персонажа — полный портрет; кружки списков (orb) — превью image_thumb_url, если оно есть */
      assetUrls()[c.img] = direct;
      c.orb = portrait(safeMediaUrl(card.image_thumb_url) || direct, c.y) ?? c.orb;
      return;
    }
    const lease = leaseArtwork(epoch, `${card.id}:${card.version}`, () => endpoints.avatarCatalog.image(card.id), index);
    leases.push(lease);
    lease.url.then(
      url => {
        /* сессия сменилась или персонажи загружены заново — прежний портрет не показывается */
        if (!leases.includes(lease) || (endpoints.sessionEpoch?.() ?? 0) !== epoch) return;
        assetUrls()[c.img] = url;
        c.orb = `url(${url}) 50% ${c.y}%/cover no-repeat`;
      },
      () => {},
    );
  });
}

/** Каталог персонажей сервера; ошибка или пустой ответ — вкладки «Персонажи» нет, как раньше */
export async function loadServerChars(portrait: (url: string | null, y: number) => string | null): Promise<ShellChar[]> {
  try {
    const catalog = await endpoints.avatarCatalog.list(catalogLocale());
    const chars = toShellChars(catalog, portrait);
    leasePortraits(catalog, chars, portrait);
    return chars;
  } catch {
    return [];
  }
}

/** Дополнить персонажа карточкой сервера (один раз); true — что-то изменилось */
export function applyDetails(c: ShellChar, d: AvatarDetails): boolean {
  c.details = true;
  const long = d.long_description.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
  const changed = long.join('\n') !== c.long.join('\n') || d.role !== c.role || d.temper !== c.temper;
  c.long = long;
  c.role = d.role;
  c.temper = d.temper;
  return changed;
}

const inflight = new Map<number, Promise<boolean>>();
/** Карточка персонажа: догрузить подробности; повторные вызовы ждут тот же запрос.
    cards — строки голосов персонажа из той же карточки (voice_cards): true — какие-то голоса добавились */
export function loadCharDetails(c: ShellChar, cards?: (voices: NonNullable<AvatarDetails['voice_cards']>) => boolean): Promise<boolean> {
  if (c.details) return Promise.resolve(false);
  let p = inflight.get(c.id);
  if (!p) {
    p = endpoints.avatarCatalog
      .details(c.id, catalogLocale())
      .then(d => {
        const added = !!d.voice_cards?.length && !!cards?.(d.voice_cards);
        return applyDetails(c, d) || added;
      }, () => false)
      .finally(() => inflight.delete(c.id));
    inflight.set(c.id, p);
  }
  return p;
}

/** Голоса, которых нет в каталоге (404): больше не запрашиваются */
const voicesGone = new Set<number>();
const voicesLoading = new Map<number, Promise<boolean>>();
/** Сбой сети или сессии — не «голоса нет»: такой голос запрашивается снова при следующем открытии карточки, один повтор — сразу */
const fetchOnce = <T>(fetchVoice: (id: number) => Promise<T>, id: number): Promise<T | null> =>
  fetchVoice(id).catch(() => fetchVoice(id)).catch((error: unknown) => {
    if ((error as { status?: number } | null)?.status === 404) voicesGone.add(id);
    return null;
  });
/** Голоса персонажа, которых ещё нет в снимке каркаса (у шаблона все голоса персонажа известны сразу; у сервера — только первая страница каталога):
    карточки GET /app/catalog/voices/{id} (§4.4) — по ним карточка персонажа считает «Голосов: N» и рисует «Голоса персонажа» целиком.
    Голос, которого нет в каталоге (404), пропускается; не пришедший по другой причине догружается при следующем открытии,
    иначе один сбой оставлял бы «Голосов: 0» до перезапуска. Повторные вызовы во время загрузки ждут ту же загрузку. true — что-то добавилось */
export function loadCharVoices<T extends { id: number }>(c: ShellChar, known: (id: number) => boolean, fetchVoice: (id: number) => Promise<T>,
  add: (voice: T) => void): Promise<boolean> {
  const missing = c.voices.filter(id => !known(id) && !voicesGone.has(id));
  if (!missing.length) return Promise.resolve(false);
  let p = voicesLoading.get(c.id);
  if (!p) {
    p = (Promise.all(missing.map(id => fetchOnce(fetchVoice, id))) as Promise<Array<T | null>>).then(got => {
      /* пока шли запросы, голос мог прийти страницей каталога — второй раз он не добавляется */
      const fresh = got.filter((voice): voice is T => voice != null && !known(voice.id));
      fresh.forEach(add);
      return fresh.length > 0;
    }).finally(() => voicesLoading.delete(c.id));
    voicesLoading.set(c.id, p);
  }
  return p;
}
